/**
 * Order Creation Composable
 * Cent-basierte Bestellerstellung mit reaktiver State-Verwaltung
 * ✅ REFACTORED: Uses RestaurantApiClient instead of deleted OrderApiService
 */
import { ref, computed, inject } from 'vue';
import { MoneyUtils } from '@shared';
export function useOrderCreation() {
    // ✅ Inject API Client from Vue app context
    const apiClient = inject('apiClient');
    if (!apiClient) {
        console.error('❌ [useOrderCreation] API Client not found in inject context');
    }
    // State
    const isCreatingOrder = ref(false);
    const orderCreationError = ref(null);
    const createdOrder = ref(null);
    // Computed Properties
    const hasCreationError = computed(() => {
        return !!orderCreationError.value;
    });
    const isOrderCreated = computed(() => {
        return !!createdOrder.value;
    });
    const createdOrderNumber = computed(() => {
        return createdOrder.value?.data?.orderNumber || null;
    });
    const createdOrderTotal = computed(() => {
        return createdOrder.value?.data?.totalFormatted || null;
    });
    // Methods
    const createOrderCent = async (orderData) => {
        try {
            isCreatingOrder.value = true;
            orderCreationError.value = null;
            console.log(`🛒 [useOrderCreation] Creating cent-based order:`, {
                subtotalCent: orderData.subtotalCent,
                totalCent: orderData.totalCent,
                items: orderData.items.length
            });
            // ✅ Validiere Order Data vor dem Senden (basic validation)
            if (!orderData.customer?.name || !orderData.customer?.email) {
                throw new Error('Customer name and email are required');
            }
            if (!orderData.items || orderData.items.length === 0) {
                throw new Error('Order must contain at least one item');
            }
            if (!apiClient) {
                throw new Error('API Client not available');
            }
            // ✅ Use RestaurantApiClient.createOrder() instead of OrderApiService
            const response = await apiClient.createOrder(orderData);
            if (response.status === 'success' && response.data) {
                const orderResponse = {
                    success: true,
                    message: 'Order created successfully',
                    data: {
                        orderNumber: response.data.orderNumber,
                        totalAmountCent: response.data.totalAmountCent,
                        totalFormatted: response.data.totalFormatted,
                        ...response.data
                    }
                };
                createdOrder.value = orderResponse;
                console.log(`✅ [useOrderCreation] Order created successfully:`, {
                    orderNumber: orderResponse.data.orderNumber,
                    totalCent: orderResponse.data.totalAmountCent,
                    totalFormatted: orderResponse.data.totalFormatted
                });
                return orderResponse;
            }
            else {
                throw new Error(response.message || 'Order creation failed');
            }
        }
        catch (error) {
            console.error(`❌ [useOrderCreation] Order creation failed:`, error);
            orderCreationError.value = error.message || 'Order creation failed';
            createdOrder.value = null;
            return null;
        }
        finally {
            isCreatingOrder.value = false;
        }
    };
    /**
     * Legacy: Erstellt Order aus Euro-basierten Daten
     * @deprecated Verwende createOrderCent für präzise Berechnungen
     */
    const createOrderFromEuro = async (euroOrderData) => {
        console.warn(`⚠️ [useOrderCreation] Using deprecated Euro-based order creation.`);
        try {
            // ✅ Basic conversion: Euro to Cent (multiply by 100)
            const centOrderData = {
                ...euroOrderData,
                deliveryFeeCent: Math.round((euroOrderData.deliveryFee || 0) * 100),
                serviceFeeCent: Math.round((euroOrderData.serviceFee || 0) * 100),
                subtotalCent: Math.round((euroOrderData.subtotal || 0) * 100),
                totalCent: Math.round((euroOrderData.total || 0) * 100),
                taxAmountCent: Math.round((euroOrderData.taxAmount || 0) * 100),
                discountAmountCent: euroOrderData.discountAmount ? Math.round(euroOrderData.discountAmount * 100) : undefined
            };
            return await createOrderCent(centOrderData);
        }
        catch (error) {
            console.error(`❌ [useOrderCreation] Euro order conversion failed:`, error);
            orderCreationError.value = error.message || 'Order creation failed';
            return null;
        }
    };
    const clearOrderCreation = () => {
        isCreatingOrder.value = false;
        orderCreationError.value = null;
        createdOrder.value = null;
    };
    const clearError = () => {
        orderCreationError.value = null;
    };
    // Helper Methods für UI Integration
    const formatOrderSummary = computed(() => {
        if (!createdOrder.value)
            return '';
        const { data } = createdOrder.value;
        return `Bestellung ${data.orderNumber} erfolgreich erstellt. Total: ${data.totalFormatted}`;
    });
    const getOrderCreationProgress = computed(() => {
        if (hasCreationError.value) {
            return {
                step: 'error',
                message: orderCreationError.value || 'Unknown error'
            };
        }
        if (isOrderCreated.value) {
            return {
                step: 'success',
                message: formatOrderSummary.value
            };
        }
        if (isCreatingOrder.value) {
            return {
                step: 'creating',
                message: 'Bestellung wird erstellt...'
            };
        }
        return {
            step: 'idle',
            message: 'Bereit zur Bestellung'
        };
    });
    // Development Helpers
    const debugOrderData = (orderData) => {
        if (process.env.NODE_ENV === 'development') {
            console.log(`🔍 [useOrderCreation] Debug Order Data:`, {
                customer: orderData.customer,
                itemCount: orderData.items.length,
                orderType: orderData.orderType,
                pricing: {
                    subtotalCent: orderData.subtotalCent,
                    subtotalFormatted: MoneyUtils.formatCurrency(orderData.subtotalCent),
                    deliveryFeeCent: orderData.deliveryFeeCent,
                    deliveryFeeFormatted: MoneyUtils.formatCurrency(orderData.deliveryFeeCent),
                    serviceFeeCent: orderData.serviceFeeCent,
                    serviceFeeFormatted: MoneyUtils.formatCurrency(orderData.serviceFeeCent),
                    totalCent: orderData.totalCent,
                    totalFormatted: MoneyUtils.formatCurrency(orderData.totalCent)
                }
            });
        }
    };
    return {
        // State
        isCreatingOrder: isCreatingOrder,
        orderCreationError: orderCreationError,
        createdOrder: createdOrder,
        // Computed
        hasCreationError,
        isOrderCreated,
        createdOrderNumber,
        createdOrderTotal,
        formatOrderSummary,
        getOrderCreationProgress,
        // Methods
        createOrderCent,
        createOrderFromEuro,
        clearOrderCreation,
        clearError,
        // Development Helpers
        ...(process.env.NODE_ENV === 'development' && { debugOrderData })
    };
}
export default useOrderCreation;
